import { useEffect, useState } from "react";
import { api, asArray } from "@/lib/api";
import { KPI, Ring, StatusBadge } from "@/components/Primitives";
import { GanttChart, Users, ShieldAlert } from "lucide-react";

// Client Intelligence — one destination for the three business views that used
// to be three separate nav items (MSP BI, Customer Success, Customer Risk),
// each rendering a single endpoint. A technician looking for "how is this
// client doing" had to guess which of the three had the number.

const TABS = [
  { key: "book", label: "Estate", icon: GanttChart },
  { key: "health", label: "Client health", icon: Users },
  { key: "risk", label: "Service risk", icon: ShieldAlert },
];

export default function Business() {
  const [tab, setTab] = useState("book");
  const [bi, setBi] = useState(null);
  const [cs, setCs] = useState([]);
  const [risk, setRisk] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [a, b, c, d] = await Promise.all([
          api.get("/dashboard/msp-bi"),
          api.get("/dashboard/customer-success"),
          api.get("/risk/customers"),
          api.get("/dashboard/executive"),
        ]);
        setBi({ ...(a.data || {}), ...(d.data?.counters || {}) });
        setCs(asArray(b.data));
        setRisk(asArray(c.data));
        setErr("");
      } catch (e) {
        // an unreachable view must not render as an empty one
        setErr(e?.response?.data?.detail || e.message || "Could not load client intelligence");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="p-6" data-testid="business-page">
      <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-mono">Intelligence</div>
      <h1 className="text-lg font-display font-semibold text-zinc-50 mt-1">Client Intelligence</h1>
      <div className="text-xs text-zinc-500">Estate posture, service health and risk across every client</div>

      <div className="flex gap-1 mt-5 border-b border-zinc-900">
        {TABS.map((t) => {
          const Icon = t.icon;
          const on = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`biz-tab-${t.key}`}
              className={`flex items-center gap-2 px-3.5 py-2 text-[13px] border-b-2 -mb-px transition-colors ${
                on ? "border-blue-500 text-zinc-100" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {err && (
        <div className="mt-5 border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm">{err}</div>
      )}
      {loading && <div className="mt-8 text-zinc-600 text-sm">Loading…</div>}

      {!loading && !err && tab === "book" && bi && (
        <>
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI label="Clients" value={bi.clients ?? "—"} testid="bi-clients" />
            <KPI label="Endpoints" value={bi.total_assets ?? "—"} sub={`${bi.online_assets ?? 0} online`} testid="bi-endpoints" />
            <KPI label="Open tickets" value={bi.open_tickets ?? "—"}
              tone={bi.critical_tickets ? "warn" : "default"}
              sub={bi.critical_tickets ? `${bi.critical_tickets} critical` : ""} testid="bi-open" />
            <KPI label="Ticket close rate" value={bi.ticket_close_rate_pct == null ? "—" : `${bi.ticket_close_rate_pct}%`}
              tone={bi.ticket_close_rate_pct >= 80 ? "good" : bi.ticket_close_rate_pct >= 50 ? "warn" : "bad"} testid="bi-close-rate" />
            <KPI label="Contracts expiring 90d" value={bi.contracts_expiring_90d ?? "—"}
              tone={bi.contracts_expiring_90d ? "warn" : "good"} testid="bi-contracts" />
            <KPI label="Backups failed" value={bi.backups_failed ?? "—"}
              tone={bi.backups_failed ? "bad" : "good"} testid="bi-backups" />
            <KPI label="Security findings" value={bi.security_findings ?? "—"} testid="bi-sec" />
            <KPI label="Compliance findings" value={bi.compliance_findings ?? "—"} testid="bi-comp" />
          </div>
        </>
      )}

      {!loading && !err && tab === "health" && (
        <div className="mt-5 bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-900">
                <th className="text-left px-4 py-2.5">Client</th>
                <th className="text-left px-4 py-2.5">Health</th>
                <th className="text-left px-4 py-2.5">Open tickets</th>
                <th className="text-left px-4 py-2.5">Endpoints</th>
                <th className="text-left px-4 py-2.5">Risk</th>
              </tr>
            </thead>
            <tbody>
              {!cs.length && <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-600">No clients yet.</td></tr>}
              {cs.map((c) => (
                <tr key={c.client_id || c.id} className="border-b border-zinc-900/70 hover:bg-zinc-900/40">
                  <td className="px-4 py-2.5 text-zinc-200">{c.name || "—"}
                    {c.industry && <div className="text-[11px] text-zinc-600">{c.industry}</div>}</td>
                  <td className="px-4 py-2.5 font-mono">{c.health_score ?? "—"}</td>
                  <td className="px-4 py-2.5 font-mono">{c.open_tickets ?? "—"}</td>
                  <td className="px-4 py-2.5 font-mono">{c.asset_count ?? "—"}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={c.renewal_risk || "active"} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !err && tab === "risk" && (
        <div className="mt-5 grid md:grid-cols-2 gap-3">
          {!risk.length && <div className="text-zinc-600 text-sm">No clients scored yet.</div>}
          {risk.map((c) => (
            <div key={c.client_id} className="bg-zinc-950 border border-zinc-900 rounded-md p-4 flex gap-4 items-start">
              <Ring value={100 - (c.risk_score ?? 0)} size={64} />
              <div className="min-w-0">
                <div className="text-zinc-100 font-medium">{c.client_name}</div>
                <div className="text-[11px] uppercase tracking-wider font-mono mt-0.5"
                  style={{ color: c.risk_level === "high" ? "#f87171" : c.risk_level === "medium" ? "#fbbf24" : "#34d399" }}>
                  {c.risk_level} risk · score {c.risk_score}
                </div>
                <ul className="mt-2 space-y-0.5">
                  {Object.entries(c.factors || {}).filter(([, v]) => v).map(([k, v]) => (
                    <li key={k} className="text-[12.5px] text-zinc-400">
                      <span className="font-mono text-zinc-300">{v}</span>{" "}
                      {k.replaceAll("_", " ")}
                    </li>
                  ))}
                </ul>
                {c.renewal_risk && <div className="text-[12px] text-amber-400 mt-2">Service risk: {c.renewal_risk}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
